import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    signal,
    WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { componentsInclude } from '../../helpers/bottom-menu.utils';
import { BottomMenuService } from '../../services/bottom-menu.service';

import { ResizeChange, ResizeDirective } from '@interacta-shared/ui';
import {
    BOTTOM_MENU_ITEM_IDS,
    BottomMenuItemId,
} from '../../models/bottom-menu-state.model';

@Component({
    selector: 'interacta-bottom-menu',
    imports: [CommonModule, ResizeDirective],
    templateUrl: './bottom-menu.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BottomMenuComponent {
    private readonly bottomMenuService = inject(BottomMenuService);

    readonly menuComponents = toSignal(
        this.bottomMenuService.getComponentsStream(),
        {
            initialValue: [],
        },
    );

    private readonly componentsSizeMap: Record<
        BottomMenuItemId,
        WritableSignal<ResizeChange['width']>
    > = {
        [BOTTOM_MENU_ITEM_IDS.AI_FAB]: signal(0),
        [BOTTOM_MENU_ITEM_IDS.BULK_WORKFLOW_TRANSITION_BANNER]: signal(0),
        [BOTTOM_MENU_ITEM_IDS.CHAT_PANEL]: signal(0),
        [BOTTOM_MENU_ITEM_IDS.UPLOAD_DIALOG]: signal(0),
        [BOTTOM_MENU_ITEM_IDS.I_LAB_RECORDER]: signal(0),
    };

    readonly componentsClassesMap: Record<BottomMenuItemId, string> = {
        [BOTTOM_MENU_ITEM_IDS.BULK_WORKFLOW_TRANSITION_BANNER]: 'z-20',
        [BOTTOM_MENU_ITEM_IDS.CHAT_PANEL]:
            'z-auto transition-all duration-250 ',
        [BOTTOM_MENU_ITEM_IDS.AI_FAB]: 'right-0 z-10',
        [BOTTOM_MENU_ITEM_IDS.UPLOAD_DIALOG]: 'z-20',
        [BOTTOM_MENU_ITEM_IDS.I_LAB_RECORDER]: 'z-20',
    };

    private readonly chatIsOpen = computed(() =>
        this.menuComponents()
            .find(({ id }) => id === BOTTOM_MENU_ITEM_IDS.CHAT_PANEL)
            ?.isOpen(),
    );
    private readonly bulkIsOpen = computed(() =>
        this.menuComponents()
            .find(
                ({ id }) =>
                    id === BOTTOM_MENU_ITEM_IDS.BULK_WORKFLOW_TRANSITION_BANNER,
            )
            ?.isOpen(),
    );
    private readonly aiIsPanelOpen = computed(() =>
        this.menuComponents()
            .find(({ id }) => id === BOTTOM_MENU_ITEM_IDS.AI_FAB)
            ?.isOpen(),
    );
    private readonly uploadIsOpen = computed(() =>
        this.menuComponents()
            .find(({ id }) => id === BOTTOM_MENU_ITEM_IDS.UPLOAD_DIALOG)
            ?.isOpen(),
    );

    readonly componentsRightMap = computed(() => {
        const GAP_WIDTH = 32;
        const aiWidth = this.componentsSizeMap[BOTTOM_MENU_ITEM_IDS.AI_FAB]();
        const chatWidth =
            this.componentsSizeMap[BOTTOM_MENU_ITEM_IDS.CHAT_PANEL]();
        const bulkWidth =
            this.componentsSizeMap[
                BOTTOM_MENU_ITEM_IDS.BULK_WORKFLOW_TRANSITION_BANNER
            ]();
        const uploadWidth =
            this.componentsSizeMap[BOTTOM_MENU_ITEM_IDS.UPLOAD_DIALOG]();

        const components = this.menuComponents();

        const aiIncluded = componentsInclude(
            components,
            BOTTOM_MENU_ITEM_IDS.AI_FAB,
        );

        const chatIncluded = componentsInclude(
            components,
            BOTTOM_MENU_ITEM_IDS.CHAT_PANEL,
        );

        const bulkIncluded = componentsInclude(
            components,
            BOTTOM_MENU_ITEM_IDS.BULK_WORKFLOW_TRANSITION_BANNER,
        );

        const uploadIncluded = componentsInclude(
            components,
            BOTTOM_MENU_ITEM_IDS.UPLOAD_DIALOG,
        );

        const chatPanelRight = aiIncluded ? aiWidth + GAP_WIDTH : GAP_WIDTH;

        const bulkBannerRight =
            chatIncluded && this.chatIsOpen()
                ? chatPanelRight + chatWidth + GAP_WIDTH
                : chatPanelRight;

        const uploadRight =
            bulkIncluded && this.bulkIsOpen()
                ? bulkBannerRight + bulkWidth + GAP_WIDTH
                : bulkBannerRight;

        const iLabRight =
            uploadIncluded && this.uploadIsOpen()
                ? uploadRight + uploadWidth + GAP_WIDTH
                : uploadRight;

        return {
            [BOTTOM_MENU_ITEM_IDS.UPLOAD_DIALOG]: `${uploadRight}px`,
            [BOTTOM_MENU_ITEM_IDS.I_LAB_RECORDER]: `${iLabRight}px`,
            [BOTTOM_MENU_ITEM_IDS.BULK_WORKFLOW_TRANSITION_BANNER]: `${bulkBannerRight}px`,
            [BOTTOM_MENU_ITEM_IDS.CHAT_PANEL]: `${chatPanelRight}px`,
            [BOTTOM_MENU_ITEM_IDS.AI_FAB]: '0px',
        };
    });

    /**
     * When the AI panel opens, we need to prevent `componentsSizeMap` from being modified,
     * otherwise the other two panels will shift to the left of the AI panel
     */
    onChangeResize(id: BottomMenuItemId, event: ResizeChange): void {
        if (id === BOTTOM_MENU_ITEM_IDS.AI_FAB && this.aiIsPanelOpen()) {
            return;
        }

        this.componentsSizeMap[id].set(event.width);
    }
}
